import React, {useEffect, useMemo, useRef, useState} from 'react';
import {handleRecoClick} from "../../common/services";

/**
 * Reusable multi-item carousel with horizontal and vertical orientations.
 * Props:
 * - orientation: 'horizontal' | 'vertical' (default: 'horizontal')
 * - itemsPerGroup: number | 'auto' (default: 2). When 'auto' in horizontal mode, items per group
 *   are computed from available width.
 * - autoAdvanceTime: number ms (default: 3000)
 * - items: array of { id, title, image } (default: demo list)
 * - showDots: boolean (default: true). Controls visibility of pagination dots (both orientations).
 * - dotSize: 'xs' | 'sm' | 'md' (default: 'sm'). Controls horizontal dot size only.
 */
const MultiItemCarousel = ({
                               orientation = 'horizontal',
                               itemsPerGroup = 2,
                               autoAdvanceTime = 3000,
                               items: itemsProp,
                               className = '',
                               title,
                               showDots = true,
                               dotSize = 'sm',
                               query,
                               clientKey,
                               recoSrc = 'voice',
                               showTitle = true,
                           }) => {
    const fallbackItems = useMemo(() => ([
        {id: 1, title: 'Product 1', image: 'https://via.placeholder.com/200x150/ff6b6b/ffffff?text=Item+1'},
        {id: 2, title: 'Product 2', image: 'https://via.placeholder.com/200x150/4ecdc4/ffffff?text=Item+2'},
        {id: 3, title: 'Product 3', image: 'https://via.placeholder.com/200x150/45b7d1/ffffff?text=Item+3'},
        {id: 4, title: 'Product 4', image: 'https://via.placeholder.com/200x150/96ceb4/ffffff?text=Item+4'},
        {id: 5, title: 'Product 5', image: 'https://via.placeholder.com/200x150/feca57/ffffff?text=Item+5'},
        {id: 6, title: 'Product 6', image: 'https://via.placeholder.com/200x150/ff9ff3/ffffff?text=Item+6'},
    ]), []);

    const items = Array.isArray(itemsProp) && itemsProp.length ? itemsProp : fallbackItems;

    const isVertical = orientation === 'vertical';
    const isAuto = !isVertical && (itemsPerGroup === 'auto' || itemsPerGroup === 0 || itemsPerGroup == null);

    const containerRef = useRef(null);
    const [computedPerGroup, setComputedPerGroup] = useState(2);

    const resolvePerGroup = () => Math.max(1, isAuto ? computedPerGroup : itemsPerGroup);
    const [currentGroup, setCurrentGroup] = useState(0);

    const perGroup = resolvePerGroup();
    const totalGroups = Math.max(1, Math.ceil(items.length / perGroup));

    useEffect(() => {
        if (totalGroups <= 1 || autoAdvanceTime <= 0) return;
        const timer = setInterval(() => {
            setCurrentGroup((prev) => (prev + 1) % totalGroups);
        }, autoAdvanceTime);
        return () => clearInterval(timer);
    }, [totalGroups, autoAdvanceTime]);

    useEffect(() => {
        setCurrentGroup(0);
    }, [items.length, perGroup]);

    useEffect(() => {
        if (!isAuto) return;
        const el = containerRef.current;
        if (!el) return;

        const GAP = 8;
        const CARD_MIN = 80;

        const compute = () => {
            const width = el.clientWidth || 0;
            const per = Math.max(1, Math.floor((width + GAP) / (CARD_MIN + GAP)));
            setComputedPerGroup(per);
        };

        compute();

        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => compute());
            ro.observe(el);
        } else {
            window.addEventListener('resize', compute);
        }
        return () => {
            if (ro) {
                ro.disconnect();
            } else {
                window.removeEventListener('resize', compute);
            }
        };
    }, [isAuto, orientation]);

    const getCurrentItems = () => {
        const startIndex = currentGroup * perGroup;
        return items.slice(startIndex, startIndex + perGroup);
    };

    const currentItems = getCurrentItems();
    const missingCount = Math.max(0, perGroup - currentItems.length);

    const dotSizeMap = {
        xs: {btn: 'w-1.5 h-1.5', gap: 'gap-1'},
        sm: {btn: 'w-2 h-2', gap: 'gap-1.5'},
        md: {btn: 'w-2.5 h-2.5', gap: 'gap-2'},
    };
    const hDot = dotSizeMap[dotSize] || dotSizeMap.sm;


    return (
        <div className={`w-full ${className}`}>
            {title && (
                <h2 className="text-xl font-semibold text-gray-800 mb-3 text-center">{title}</h2>
            )}

            <div ref={containerRef}
                 className={`relative overflow-hidden bg-gray-50 rounded-lg ${isVertical ? 'p-4 pl-12' : 'p-0'} mb-4`}>
                <div
                    className={`flex ${isVertical ? 'flex-col items-center gap-4 min-h-[200px]' : 'justify-center gap-2 min-h-[4rem]'}`}>
                    {currentItems.map((item, index) => {
                        const globalIndex = currentGroup * perGroup + index;
                        const onItemClick = () => handleRecoClick(item, {
                            index: globalIndex,
                            query,
                            clientKey,
                            recoSrc
                        });
                        return (
                            <div
                                key={`${currentGroup}-${item.id}-${index}`}
                                className={`flex-shrink-0 bg-white rounded-lg shadow ${isVertical ? 'p-3' : 'p-0.5'} transition-all duration-500 ease-in-out cursor-pointer max-h-fit`}
                                style={{
                                    animation: `${isVertical ? 'fadeInSlideY' : 'fadeInSlideX'} 0.6s ease-out ${index * 0.1}s both`
                                }}
                                onClick={onItemClick}
                            >
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className={`${isVertical ? 'w-48 h-32' : 'w-[3.5rem] h-[3.5rem] sm:w-24 sm:h-24'} object-contain rounded-md md:mb-1`}
                                />
                                {showTitle && (
                                    <h3 className={`${isVertical ? 'text-xs' : 'text-[10px] sm:text-xs truncate w-20 sm:w-24 mx-auto leading-snug h-3 sm:h-4'} font-semibold text-gray-800 text-center ${isVertical ? '' : ''}`}>
                                        {item.title}
                                    </h3>
                                )}
                            </div>
                        );
                    })}
                    {Array.from({length: missingCount}, (_, i) => (
                        <div
                            key={`ph-${currentGroup}-${i}`}
                            className={`flex-shrink-0 bg-white rounded-lg shadow ${isVertical ? 'p-3' : 'p-0.5'} invisible max-h-fit`}
                        >
                            <div
                                className={`${isVertical ? 'w-48 h-32' : 'w-[3.5rem] h-[3.5rem] sm:w-24 sm:h-24'} rounded-md mb-1`}></div>
                            <div className="h-4 hidden md:block"></div>
                        </div>
                    ))}
                </div>
                {isVertical && showDots && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                        {Array.from({length: totalGroups}, (_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentGroup(index)}
                                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                                    currentGroup === index ? 'bg-[#9C95DC] scale-125' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                                aria-label={`Go to group ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {!isVertical && showDots && (
                <div className={`flex justify-center ${hDot.gap}`}>
                    {Array.from({length: totalGroups}, (_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentGroup(index)}
                            className={`${hDot.btn} rounded-full transition-all duration-300 ${
                                currentGroup === index ? 'bg-[#9C95DC] scale-110' : 'bg-gray-300 hover:bg-gray-400'
                            }`}
                            aria-label={`Go to group ${index + 1}`}
                        />
                    ))}
                </div>
            )}

            <style>{`
        @keyframes fadeInSlideY {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeInSlideX {
          0% { opacity: 0; transform: translateX(20px) scale(0.95); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>
        </div>
    );
};

export default MultiItemCarousel;
